import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, FileText, Plus, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const leaveTypes = [
  { value: 'Casual Leave', label: 'Casual Leave (CL)', color: 'blue' },
  { value: 'Sick Leave', label: 'Sick Leave (SL)', color: 'red' },
  { value: 'Earned Leave', label: 'Earned Leave (EL)', color: 'green' },
  { value: 'Half Day', label: 'Half Day', color: 'yellow' }
];

export default function StaffLeave() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leaveData, setLeaveData] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    leave_type: 'Casual Leave',
    from_date: '',
    to_date: '',
    reason: ''
  });

  useEffect(() => {
    fetchLeaveData();
  }, []);

  const fetchLeaveData = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        navigate('/staff-portal');
        return;
      }

      const response = await fetch(`${API_URL}/api/staff-portal/leave`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLeaveData(data);
      }
    } catch (error) {
      console.error('Error fetching leave data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    
    if (!formData.from_date || !formData.to_date) {
      toast.error('Please select dates');
      return;
    }

    if (new Date(formData.from_date) > new Date(formData.to_date)) {
      toast.error('From date cannot be after To date');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('staffToken');
      const response = await fetch(`${API_URL}/api/staff-portal/leave/apply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Leave application submitted successfully!');
        setShowApplyModal(false);
        setFormData({ leave_type: 'Casual Leave', from_date: '', to_date: '', reason: '' });
        fetchLeaveData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit leave application');
      }
    } catch (error) {
      console.error('Error applying leave:', error);
      toast.error('Failed to submit leave application');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'Rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'Pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-700';
      case 'Rejected':
        return 'bg-red-100 text-red-700';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const calculateDays = (from, to) => {
    if (!from || !to) return 0;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    return Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const balance = leaveData?.balance || {};
  const requests = leaveData?.requests || [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="staff-leave-page">
      {/* Header */}
      <header className="bg-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/staff-portal/dashboard" className="p-2 hover:bg-indigo-600 rounded-lg">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold">Leave Management</h1>
                <p className="text-indigo-200 text-sm">View balance and apply for leave</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowApplyModal(true)}
              className="bg-white text-indigo-700 hover:bg-indigo-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Apply Leave
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Leave Balance Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-blue-600 font-medium">Casual Leave</p>
              <p className="text-3xl font-bold text-blue-700 mt-1">
                {(balance.casual_leave || 12) - (balance.used_casual || 0)}
              </p>
              <p className="text-xs text-blue-500">of {balance.casual_leave || 12} remaining</p>
            </CardContent>
          </Card>

          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-red-600 font-medium">Sick Leave</p>
              <p className="text-3xl font-bold text-red-700 mt-1">
                {(balance.sick_leave || 10) - (balance.used_sick || 0)}
              </p>
              <p className="text-xs text-red-500">of {balance.sick_leave || 10} remaining</p>
            </CardContent>
          </Card>

          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-green-600 font-medium">Earned Leave</p>
              <p className="text-3xl font-bold text-green-700 mt-1">
                {(balance.earned_leave || 15) - (balance.used_earned || 0)}
              </p>
              <p className="text-xs text-green-500">of {balance.earned_leave || 15} remaining</p>
            </CardContent>
          </Card>

          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-purple-600 font-medium">Total Used</p>
              <p className="text-3xl font-bold text-purple-700 mt-1">
                {(balance.used_casual || 0) + (balance.used_sick || 0) + (balance.used_earned || 0)}
              </p>
              <p className="text-xs text-purple-500">this year</p>
            </CardContent>
          </Card>
        </div>

        {/* Leave History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Leave History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {requests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No leave applications yet</p>
                <Button onClick={() => setShowApplyModal(true)} className="mt-4" variant="outline">
                  Apply for Leave
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {requests.map((request, index) => (
                  <div
                    key={request.id || index}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      {getStatusIcon(request.status)}
                      <div>
                        <p className="font-medium text-gray-900">{request.leave_type}</p>
                        <p className="text-sm text-gray-500">
                          {formatDate(request.from_date)} - {formatDate(request.to_date)}
                          <span className="ml-2 text-indigo-600">
                            ({calculateDays(request.from_date, request.to_date)} days)
                          </span>
                        </p>
                        {request.reason && (
                          <p className="text-xs text-gray-400 mt-1">{request.reason}</p>
                        )}
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                      {request.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Apply Leave Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">Apply for Leave</h3>
              <button onClick={() => setShowApplyModal(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            
            <form onSubmit={handleApplyLeave} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
                <select
                  value={formData.leave_type}
                  onChange={(e) => setFormData({...formData, leave_type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {leaveTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                  <input
                    type="date"
                    value={formData.from_date}
                    onChange={(e) => setFormData({...formData, from_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                  <input
                    type="date"
                    value={formData.to_date}
                    onChange={(e) => setFormData({...formData, to_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min={formData.from_date || new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              {formData.from_date && formData.to_date && (
                <div className="bg-indigo-50 p-3 rounded-lg text-center">
                  <span className="text-indigo-700 font-medium">
                    {calculateDays(formData.from_date, formData.to_date)} day(s) leave
                  </span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Enter reason for leave..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowApplyModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
